import type { MessageDescriptor } from 'react-intl';

import clsx from 'clsx';
import { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { ToastLinkProps, ToastProps, ToastStatus } from './types';

import { getStatusIcon } from '../../utils/getStatusIcon';
import { IconButton } from '../Button/subcomponents/IconButton';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import './Toast.css';
import { Link } from '../Link/Link';
import { Text } from '../Text/Text';
import { getToastRole } from './utils';

const messages = defineMessages({
  dismiss: {
    id: 'Toast.dismiss',
    defaultMessage: 'Dismiss',
    description: 'Accessible name for the toast dismiss button',
  },
  statusCritical: {
    id: 'Toast.statusCritical',
    defaultMessage: 'Error',
    description: 'Accessible name for a critical toast status icon',
  },
  statusInformational: {
    id: 'Toast.statusInformational',
    defaultMessage: 'Information',
    description: 'Accessible name for an informational toast status icon',
  },
  statusPositive: {
    id: 'Toast.statusPositive',
    defaultMessage: 'Success',
    description: 'Accessible name for a positive toast status icon',
  },
});

const statusIconLabels: Record<ToastStatus, MessageDescriptor> = {
  critical: messages.statusCritical,
  informational: messages.statusInformational,
  positive: messages.statusPositive,
};

function ToastLink({ link }: { link: ToastLinkProps }) {
  if ('onClick' in link) {
    return (
      <button
        className={clsx(
          'eds-link',
          `eds-link--variant-${link.variant ?? 'default'}`,
          `eds-link--${link.size ?? 's'}`,
        )}
        onClick={link.onClick}
        type="button"
      >
        <Text __private={{ inInteractive: true }} variant="body-s">
          {link.text}
        </Text>
      </button>
    );
  }

  return (
    <Link
      external={link.external}
      href={link.href}
      size={link.size ?? 's'}
      text={link.text}
    />
  );
}

export const Toast = memo(
  ({ link, onDismiss, status = 'positive', text, title }: ToastProps) => {
    const intl = useIntl();
    const statusIcon = getStatusIcon(status);
    const dismissLabel = intl.formatMessage(messages.dismiss);

    return (
      <Layout
        align="start"
        aria-atomic="true"
        className={clsx('eds-toast', `eds-toast--status-${status}`)}
        data-testid="eds-toast"
        role={getToastRole(status)}
        spacing="s"
      >
        <span className="eds-toast--status-icon">
          <Icon
            icon={statusIcon}
            label={intl.formatMessage(statusIconLabels[status])}
            size="m"
          />
        </span>
        <Layout direction="column" flex="auto" spacing="s">
          <div className="eds-toast--message-block">
            {title ? (
              <Text as="p" variant="body-semi-s">
                {title}
              </Text>
            ) : null}
            <Text as="p" variant="body-s">
              {text}
            </Text>
          </div>
          {link ? <ToastLink link={link} /> : null}
        </Layout>
        {onDismiss ? (
          <IconButton
            icon={{ key: 'x', label: dismissLabel }}
            label={dismissLabel}
            onClick={onDismiss}
            size="s"
            variant="tertiary"
          />
        ) : null}
      </Layout>
    );
  },
);

Toast.displayName = 'Toast';
